import { ZoomSlider } from '@/components/zoom-slider';
import { type AppEdge, edgeTypes } from '@/edges';
import { type AppNode, nodeTypes } from '@/nodes';
import { addingNodeState, edgesState, nextIdState, nodesState, selectedFlowIdState, selectedNodeState } from '@/state';
import { useUnwrappedAtom } from '@/state/loadableHooks';
import { type DataKeyDataType, getAllOutgoingHandles, type GuideNodeId, type OutgoingHandle } from '@rock/types';
import {
    addEdge,
    applyEdgeChanges,
    applyNodeChanges,
    Background,
    type Connection,
    type EdgeChange,
    MarkerType,
    MiniMap,
    type NodeChange,
    type OnConnect,
    ReactFlow,
    reconnectEdge,
    SelectionMode,
    useReactFlow,
} from '@xyflow/react';
import { useAtomValue } from 'jotai';
import { useSetAtom } from 'jotai/index';
import { keyBy } from 'lodash';
import { type DragEventHandler, startTransition, useCallback, useContext, useEffect } from 'react';
import { FlowParamsContext } from './FlowInitializer';

const panOnDrag = [1, 2];

const MainPanel = () => {
    const { screenToFlowPosition } = useReactFlow();

    const params = useContext(FlowParamsContext);
    const flowId = useAtomValue(selectedFlowIdState(params));
    const [nodes = [], setNodes] = useUnwrappedAtom(nodesState(params));
    const [edges = [], setEdges] = useUnwrappedAtom(edgesState(params));
    const incrementNextId = useSetAtom(nextIdState(params));
    const addingNode = useAtomValue(addingNodeState);
    const setSelectedNode = useSetAtom(selectedNodeState(params));
    const nodesById = keyBy(nodes, 'id');

    const onNodesChange = useCallback(
        async (changes: NodeChange<AppNode>[]) => {
            await setNodes(applyNodeChanges(changes, nodes));
        },
        [nodes, setNodes],
    );

    const onEdgesChange = useCallback(
        async (changes: EdgeChange<AppEdge>[]) => {
            await setEdges(applyEdgeChanges(changes, edges));
        },
        [edges, setEdges],
    );

    useEffect(() => {
        const nonOrphanedEdges = edges.filter((e) => nodesById[e.source] && nodesById[e.target]);
        if (nonOrphanedEdges.length > 0 && nonOrphanedEdges.length !== edges.length) {
            // noinspection JSIgnoredPromiseFromCall
            setEdges(nonOrphanedEdges);
        }
    }, [nodesById, edges, setEdges]);

    const onReconnect = useCallback(
        async (oldEdge: AppEdge, newConnection: Connection) => {
            await setEdges(reconnectEdge(oldEdge, newConnection, edges, { shouldReplaceId: false }));
        },
        [edges, setEdges],
    );

    const onConnect: OnConnect = useCallback(
        async (connection) => {
            const nextId = await incrementNextId();
            if (!nextId) return;

            await setEdges(
                addEdge(
                    {
                        ...connection,
                        id: `edge:${nextId}`,
                        type: 'dataKey',
                        data: {
                            type: 'dataKey',
                            items: {
                                equipment: [],
                                considerations: [],
                            },
                        } satisfies DataKeyDataType,
                    },
                    edges,
                ),
            );
        },
        [edges, setEdges, incrementNextId],
    );

    const onDragOver: DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    const onDrop: DragEventHandler<HTMLDivElement> = useCallback(
        async (event) => {
            event.preventDefault();
            if (!addingNode) return;

            const nextId = await incrementNextId();
            if (!nextId) return;

            const position = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });
            const newNode: AppNode = {
                id: `${addingNode.type}:${nextId}`,
                position,
                ...addingNode,
            };

            startTransition(async () => {
                await setNodes(nodes.concat(newNode));
                await setSelectedNode(newNode.id);
            });
        },
        [screenToFlowPosition, addingNode, nodes, setNodes, setSelectedNode, incrementNextId],
    );

    return (
        <ReactFlow
            key={flowId}
            nodes={nodes}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            edges={prepareEdges(edges, nodes)}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={{
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                },
            }}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onReconnect={onReconnect}
            edgesReconnectable={false}
            reconnectRadius={20}
            onDrop={onDrop}
            onDragOver={onDragOver}
            fitView
            // Figma-like Viewport
            panOnScroll
            selectionOnDrag
            panOnDrag={panOnDrag}
            selectionMode={SelectionMode.Partial}
        >
            <Background />
            <MiniMap position="bottom-right" />
            <ZoomSlider position="bottom-left" />
        </ReactFlow>
    );
};

function prepareEdges(edges: AppEdge[], nodes: AppNode[]): AppEdge[] {
    const handlesByNodeId: Record<GuideNodeId, OutgoingHandle[]> = {};
    for (const node of nodes) {
        handlesByNodeId[node.id as GuideNodeId] = getAllOutgoingHandles(node.id as GuideNodeId, node.data);
    }

    return edges
        .filter((edge) => {
            const nodeHandles = handlesByNodeId[edge.source as GuideNodeId];
            return nodeHandles?.includes(edge.sourceHandle as OutgoingHandle);
        })
        .map((edge) => (edge.selected ? { ...edge, reconnectable: true } : edge));
}

export default MainPanel;
